/**
 * Данные расписаний. Источник — docx-файлы школы:
 *  - «СТАРШИЕ КЛАССЫ 2026-2027.docx»
 *  - «Суббота - начальные классы.docx» (1 смена + подготовительные классы)
 *  - «Расписание кружков.docx»
 */

import { SubjectTone } from "./subjectTone";

export type Lesson = {
  /** основная строка ячейки: предмет для классов, группа для кружков */
  subject: string;
  /** подпись сверху, если занятие отличается от колонки (напр. ТЕАТР в колонке «Хор и театр») */
  label?: string;
  /** точное время, если оно отличается от временного блока строки */
  time?: string;
  /** кабинет, если занятие проходит не в кабинете класса / кружка */
  room?: string;
  note?: string;
} | null;

export type ClassColumn = {
  room: string;
  name: string;
  teacher?: string;
  /** фиксированный цвет колонки; иначе цвет подбирается по названию предмета */
  tone?: SubjectTone;
};

export type TimeRow = {
  time: string;
  lessons: Lesson[];
};

export type ClassSchedule = {
  title: string;
  subtitle?: string;
  /** подпись первой колонки, по умолчанию «Время» */
  cornerLabel?: string;
  columns: ClassColumn[];
  rows: TimeRow[];
  legend?: string;
};

/* ------------------------------------------------------------------ */
/* Старшие классы                                                      */
/* ------------------------------------------------------------------ */

export const seniorSchedule: ClassSchedule = {
  legend: "Кабинет указан рядом с уроком, если занятие проходит не в кабинете класса.",
  title: "Расписание средних и старших классов",
  subtitle: "2026 — 2027 учебный год",
  columns: [
    { room: "204 каб.", name: "5 кл.", teacher: "И.П." },
    { room: "206 каб.", name: "6 кл.", teacher: "Т.А." },
    { room: "225 каб.", name: "7 кл.", teacher: "А.Р." },
    { room: "226 каб.", name: "8 кл.", teacher: "О.Н." },
    { room: "227 каб.", name: "9 кл.", teacher: "Е.Р." },
    { room: "228 каб.", name: "10 кл.", teacher: "О.В." },
    { room: "228 каб.", name: "11 кл.", teacher: "О.Н." },
  ],
  rows: [
    {
      time: "9:00 – 9:45",
      lessons: [
        { subject: "Мировая художественная культура" },
        { subject: "Математика" },
        { subject: "Алгебра" },
        { subject: "Биология" },
        { subject: "Биология", room: "226 к." },
        { subject: "Русский язык" },
        { subject: "Физика", room: "227 к." },
      ],
    },
    {
      time: "9:55 – 10:40",
      lessons: [
        { subject: "Русский язык" },
        { subject: "Мировая художественная культура" },
        { subject: "Геометрия" },
        { subject: "Литература" },
        { subject: "Алгебра" },
        { subject: "Химия" },
        { subject: "Химия" },
      ],
    },
    {
      time: "10:55 – 11:40",
      lessons: [
        { subject: "Математика" },
        { subject: "Русский язык" },
        { subject: "Русский язык" },
        { subject: "Алгебра" },
        { subject: "Русский язык" },
        { subject: "Физика", room: "101 к." },
        { subject: "Алгебра" },
      ],
    },
    {
      time: "11:50 – 12:35",
      lessons: [
        { subject: "Математика" },
        { subject: "Литература" },
        { subject: "Литература" },
        { subject: "Физика" },
        { subject: "Литература" },
        { subject: "Биология" },
        { subject: "Биология" },
      ],
    },
    {
      time: "12:45 – 13:30",
      lessons: [
        { subject: "Литература" },
        { subject: "Математика" },
        { subject: "Физика" },
        { subject: "Геометрия" },
        { subject: "Химия" },
        { subject: "Литература" },
        { subject: "Литература" },
      ],
    },
    {
      time: "13:40 – 14:25",
      lessons: [
        null,
        null,
        { subject: "Биология" },
        { subject: "Русский язык" },
        { subject: "Физика" },
        { subject: "Алгебра" },
        { subject: "Геометрия", room: "204 к." },
      ],
    },
    {
      time: "14:30 – 15:15",
      lessons: [
        null,
        null,
        null,
        null,
        { subject: "Геометрия" },
        { subject: "Геометрия" },
        { subject: "Русский язык", room: "204 к." },
      ],
    },
  ],
};

/* ------------------------------------------------------------------ */
/* Младшие классы — суббота, 1 смена                                   */
/* ------------------------------------------------------------------ */

export const primarySchedule: ClassSchedule = {
  legend: "Кабинет указан рядом с уроком, если занятие проходит не в кабинете класса.",
  title: "Расписание младших классов",
  subtitle: "Суббота · 1 смена",
  columns: [
    { room: "102 каб.", name: "1 кл.", teacher: "И.А." },
    { room: "101 каб.", name: "2 кл.", teacher: "А.Ю." },
    { room: "201 каб.", name: "3 кл.", teacher: "О.А." },
    { room: "202 каб.", name: "4 кл.", teacher: "М.В." },
  ],
  rows: [
    {
      time: "9:00 – 9:45",
      lessons: [
        { subject: "Чтение" },
        { subject: "Чтение" },
        { subject: "Чтение" },
        { subject: "Чтение" },
      ],
    },
    {
      time: "9:55 – 10:40",
      lessons: [
        { subject: "Музыка" },
        { subject: "Русский язык" },
        { subject: "Русский язык" },
        { subject: "Русский язык" },
      ],
    },
    {
      time: "10:55 – 11:40",
      lessons: [
        { subject: "Русский язык" },
        { subject: "Музыка" },
        { subject: "Математика" },
        { subject: "Мир вокруг нас" },
      ],
    },
    {
      time: "11:50 – 12:35",
      lessons: [
        { subject: "Математика" },
        { subject: "Математика" },
        { subject: "Музыка" },
        { subject: "Математика" },
      ],
    },
    {
      time: "12:45 – 13:30",
      lessons: [
        null,
        null,
        { subject: "Мир вокруг нас" },
        { subject: "Музыка" },
      ],
    },
  ],
};

/* ------------------------------------------------------------------ */
/* Подготовительные классы — суббота                                   */
/* ------------------------------------------------------------------ */

export const preparatorySchedule: ClassSchedule = {
  legend: "Кабинет указан рядом с уроком, если занятие проходит не в кабинете класса.",
  title: "Расписание подготовительных классов",
  subtitle: "Суббота",
  columns: [
    { room: "104 каб.", name: "0 кл.", teacher: "Н.В." },
    { room: "106 каб.", name: "0-1 кл.", teacher: "А.В." },
  ],
  rows: [
    {
      time: "8:30 – 9:00",
      lessons: [{ subject: "Развитие речи" }, { subject: "Мы растем здоровыми" }],
    },
    {
      time: "9:10 – 9:40",
      lessons: [{ subject: "Музыка" }, { subject: "Развитие речи" }],
    },
    {
      time: "9:50 – 10:20",
      lessons: [{ subject: "Грамота" }, { subject: "Математика" }],
    },
    {
      time: "10:30 – 11:00",
      lessons: [{ subject: "Мы растем здоровыми" }, { subject: "Грамота" }],
    },
    {
      time: "11:10 – 11:40",
      lessons: [{ subject: "Математика" }, { subject: "Музыка" }],
    },
  ],
};

/* ------------------------------------------------------------------ */
/* Кружки и студии                                                     */
/* ------------------------------------------------------------------ */

/**
 * Кружки идут строками, как в docx: слева название студии, а время
 * подписано внутри каждой ячейки. Колонки — временные блоки, по которым
 * занятия выровнены в исходной таблице.
 */

export type ClubSlot = {
  /** название занятия, если оно отличается от строки (напр. ТЕАТР в строке «Хор и театр») */
  label?: string;
  /** одно или несколько времён занятия в этой ячейке */
  times: string[];
  group?: string;
  note?: string;
} | null;

export type ClubRow = {
  name: string;
  place?: string;
  tone: SubjectTone;
  slots: ClubSlot[];
};

export type ClubsSchedule = {
  title: string;
  subtitle?: string;
  legend?: string;
  rows: ClubRow[];
};

export const clubsSchedule: ClubsSchedule = {
  title: "Расписание студий и кружков",
  legend:
    "Время каждого занятия указано в ячейке. Уроки онлайн проходят по средам вечером.",
  rows: [
    {
      name: "Изо",
      place: "404 каб.",
      tone: "art",
      slots: [
        null,
        { times: ["11:45 – 12:40"], group: "0, 0-1 кл." },
        { times: ["12:45 – 13:40"], group: "0-1, 1-2 кл." },
        { times: ["13:45 – 14:40"], group: "3-6 кл." },
        { times: ["14:40 – 15:40"], group: "ст. классы" },
        null,
      ],
    },
    {
      name: "Танцы",
      place: "La Chapelle",
      tone: "rose",
      slots: [
        null,
        { times: ["11:45 – 12:40"], group: "0, 0-1 кл." },
        { times: ["12:45 – 13:40"], group: "0-1, 1-2 кл." },
        { times: ["13:50 – 14:45"], group: "1-2, 3-6 кл." },
        null,
        { times: ["15:40 – 16:40"], group: "ст. классы" },
      ],
    },
    {
      name: "Хор и театр",
      place: "103 каб.",
      tone: "science",
      slots: [
        null,
        null,
        { label: "Театр", times: ["12:45 – 14:40"], group: "1-2 кл.", note: "102 каб." },
        { label: "Хор", times: ["13:45 – 14:30"] },
        { label: "Театр", times: ["14:40 – 15:40"] },
        { label: "Театр", times: ["15:40 – 16:40"] },
      ],
    },
    {
      name: "Шахматы",
      place: "104 каб.",
      tone: "math",
      slots: [
        null,
        null,
        { times: ["12:45 – 13:40"], group: "1-2 кл." },
        { times: ["13:45 – 14:40"], group: "3-4 кл." },
        { times: ["14:45 – 15:40"], group: "3-5 и 6-7 кл." },
        null,
      ],
    },
    {
      name: "Английский язык",
      place: "101 каб.",
      tone: "lang",
      slots: [
        null,
        null,
        { times: ["12:45 – 13:40"], group: "1-2 кл." },
        { times: ["13:45 – 14:40"], group: "3 кл." },
        { times: ["14:45 – 15:40"], group: "4-6 кл.", note: "возможно онлайн" },
        null,
      ],
    },
    {
      name: "Робототехника",
      place: "106 каб.",
      tone: "teal",
      slots: [
        null,
        null,
        { times: ["12:45 – 13:40"], group: "1-2 классы" },
        { times: ["13:45 – 14:40"], group: "3-4 классы" },
        { times: ["14:45 – 15:40"], group: "5-8 кл." },
        null,
      ],
    },
    {
      name: "Суперкроха",
      place: "La Chapelle",
      tone: "sun",
      slots: [
        { times: ["9:00 – 10:30"], group: "1 группа" },
        null,
        null,
        null,
        null,
        null,
      ],
    },
    {
      name: "Уроки онлайн",
      place: "Подготовка в Secondaire",
      tone: "neutral",
      slots: [
        {
          label: "Среда",
          times: ["17:30 – 18:15", "18:20 – 19:05"],
          group: "5 кл.",
        },
        {
          label: "Среда",
          times: ["19:15 – 20:00", "20:05 – 20:50"],
          group: "6 кл.",
        },
        null,
        null,
        null,
        null,
      ],
    },
  ],
};
